/**
 * Arbiter Sports entity schemas.
 *
 * Mirrors the permissive Phase 0 backend Zod schemas in
 * `apps/backend/src/interfaces/arbiter/schemas.ts`. Both pass through
 * unknown fields because Arbiter's OpenAPI is incomplete (`statusId`
 * enum undocumented; some shapes diverge from the spec).
 *
 * `ArbiterTeamSectionLinkSchema` mirrors the API projection, NOT the
 * Prisma row — `id` is the cuid `publicId` and `sectionId` is the
 * section's `publicId` (not the BigInt FK).
 */

import { z } from 'zod'

// ----- Game -----

export const ArbiterGameTeamSchema = z
  .object({
    gameTeamId: z.number().optional(),
    teamId: z.number().optional(),
    teamName: z.string().optional(),
    schoolId: z.number().optional(),
    schoolName: z.string().optional(),
    levelId: z.number().optional(),
    levelName: z.string().optional(),
    isHome: z.boolean().optional(),
    score: z.number().nullable().optional(),
    isMarkedWinner: z.boolean().optional(),
    teamInstanceId: z.number().optional(),
    alias: z.string().nullable().optional()
  })
  .passthrough()

export type ArbiterGameTeam = z.infer<typeof ArbiterGameTeamSchema>

export const ArbiterGameSchema = z
  .object({
    uniqueGameId: z.number(),
    groupId: z.number().optional(),
    fromDate: z.string().optional(),
    toDate: z.string().optional(),
    timeZone: z.string().nullable().optional(),
    utcOffset: z.number().nullable().optional(),
    lastModifiedDate: z.string().optional(),
    isDeleted: z.boolean().optional(),
    levelId: z.number().nullable().optional(),
    levelName: z.string().nullable().optional(),
    sportId: z.number().nullable().optional(),
    sportName: z.string().nullable().optional(),
    genderId: z.number().nullable().optional(),
    gender: z.string().nullable().optional(),
    siteId: z.number().nullable().optional(),
    siteName: z.string().nullable().optional(),
    subSiteId: z.number().nullable().optional(),
    subSiteName: z.string().nullable().optional(),
    gameTypeId: z.number().nullable().optional(),
    gameTypeName: z.string().nullable().optional(),
    statusId: z.number().nullable().optional(),
    status: z.string().nullable().optional(),
    title: z.string().nullable().optional(),
    teams: z.array(ArbiterGameTeamSchema).optional()
  })
  .passthrough()

export type ArbiterGame = z.infer<typeof ArbiterGameSchema>

// ----- Team -----

export const ArbiterTeamSchema = z
  .object({
    teamId: z.number(),
    teamName: z.string().optional(),
    schoolId: z.number().optional(),
    sport: z.number().nullable().optional(),
    level: z.number().nullable().optional(),
    gender: z.number().nullable().optional(),
    alias: z.string().nullable().optional(),
    siteId: z.number().nullable().optional(),
    subSiteId: z.number().nullable().optional(),
    isStandardized: z.boolean().optional(),
    isDeleted: z.boolean().optional(),
    isCoOp: z.boolean().optional()
  })
  .passthrough()

export type ArbiterTeam = z.infer<typeof ArbiterTeamSchema>

// ----- Team ↔ Section link -----

export const ArbiterTeamSectionLinkSchema = z.object({
  id: z.string(), // publicId (cuid)
  schoolPublicId: z.string(),
  arbiterTeamId: z.number().int(),
  sectionPublicId: z.string(),
  arbiterTeamName: z.string().nullable().optional(),
  arbiterSport: z.string().nullable().optional(),
  arbiterLevel: z.string().nullable().optional(),
  createdBy: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string().nullable().optional()
})

export type ArbiterTeamSectionLink = z.infer<typeof ArbiterTeamSectionLinkSchema>

// ----- Error envelope (typed 502 body for Arbiter-side failures) -----

export const ArbiterErrorKindSchema = z.enum([
  'arbiter_auth',
  'arbiter_not_configured',
  'arbiter_response_invalid',
  'arbiter_unreachable',
  'arbiter_api_error'
])

export type ArbiterErrorKind = z.infer<typeof ArbiterErrorKindSchema>

export const ArbiterErrorEnvelopeSchema = z.object({
  kind: ArbiterErrorKindSchema,
  message: z.string().optional(),
  statusCode: z.number().int().optional()
})

export type ArbiterErrorEnvelope = z.infer<typeof ArbiterErrorEnvelopeSchema>
